import React from "react";
import { objectsDeepEqual } from "./SmartComponent";
import { Rnd } from "react-rnd";
import { bindActionCreators } from "redux";
import { connect } from "react-redux";
import PanelHeader from "./panel/PanelHeader";
import { getScaleForKey } from "../util/temperatureScales";
import {
  closePanel,
  setPanelLiveMode,
  setPanelPlayback,
  toggleMaximize,
  togglePanelOptions,
  touchPanel,
  updateColourCurve,
  updateFrame,
  updatePanelNextFrame,
  updatePanelPlaybackSpeed,
  updatePanelPreviousFrame,
  updatePosition,
  updateSize,
  updateTemperatureDisplay
} from "../actions/panel";
import "./styles/panel.css";
import ratios from "./panel-ratios";
import {
  calculateAndReturnSelectionForPanel,
  getPanelProperties,
  getTime
} from "../selectors";
import {
  defaultFrame,
  defaultFrameList,
  defaultImage
} from "../selectors/default";
import { calculateExtraHeight, MAX_PANEL_SIZE } from "../config";
import FrameListSelector from "./panel/FrameListSelector";
import PanelImage from "./panel/PanelImage";
import PanelFooter from "./panel/PanelFooter";

const settings = window.settings;

export const BASE_PLAYBACK_INTERVAL = 600; // base number of milliseconds per tick
export const BASE_PLAYBACK_LAST_FRAME_INTERVAL = 1000; // wait these many milliseconds once we've reached the last frame

const panelMoved = function (panelId, event, data) {
  const { x, y } = data;
  return updatePosition(panelId, { x, y });
};

const panelResized = function (panelId, _event, dir, _element, delta) {
  return updateSize(panelId, dir, delta);
};

export const calculateImageHeight = (width, ratio) => width * (1 / ratio);

/**
 * Determine if a panel pattern matches a composite key.
 *
 * @param pattern An array of four regular expressions.
 * @param compositeKey The composite key of a panel.
 * @returns Boolean True if the Array of patterns match the composite key.
 */
export function patternMatchesKey(pattern, key) {
  if (pattern.length !== 4) {
    console.error("Composite Key pattern must have four patterns.");
    return false;
  } else if (!key) {
    console.warn("No composite key.");
    return false;
  }

  const [product, subProduct, geography, subGeography] = pattern;

  return (
    product.test(key.product) &&
    subProduct.test(key.subProduct) &&
    geography.test(key.geography) &&
    subGeography.test(key.subGeography)
  );
}

/**
 * Filter all available colour curves according to the patterns defined in the database
 *
 * @param colourCurve All the enabled colour curves from the database
 * @param compositeKey The composite key of a panel
 * @returns {{list: this, map: {}}} All the colour curves that match the filters
 */
export function colourCurvesForKey(colourCurve, compositeKey) {
  const colourCurveMap = {};
  const colourCurves = [];
  for (let c of Object.values(colourCurve)) {
    if (patternMatchesKey(c.pattern, compositeKey)) {
      colourCurves.push(c);
      colourCurveMap[c.pk] = c;
    }
  }
  return {
    map: colourCurveMap,
    list: colourCurves.sort((a, b) =>
      a.label === b.label ? 0 : a.label > b.label ? 1 : -1
    )
  };
}

export function uniquePropsForPanel(props) {
  // Create an object to compare panel states. If any of these
  // properties change the panel should be updated.
  return {
    time: props.time ? props.time.format("YYYY-MM-DD HH:mm") : null,
    id: props.id,
    posX: props.panelMetaData.position.x,
    posY: props.panelMetaData.position.y,
    frameLists: props.frameList.list.map(l => l.id).join(","),
    frames: props.frame.list.map(l => l.id).join(","),
    images: props.image.list.map(l => l.id).join(","),
    activeFrameList: props.frameList.active ? props.frameList.active.id : null,
    currentFrameList: props.frameList.current
      ? props.frameList.current.id
      : null,
    activeFrame: props.frame.active ? props.frame.active.id : null,
    currentFrame: props.frame.current ? props.frame.current.id : null,
    activeImage: props.image.active ? props.image.active.id : null,
    currentImage: props.image.current ? props.image.current.id : null,
    isLive: props.panelMetaData.isLiveMode,
    isPlayback: props.panelMetaData.isPlayback,
    showOptions: props.panelMetaData.playbackPanelOpened,
    playbackSpeed: props.panelMetaData.playbackSpeedModifier,
    cropX: props.panelMetaData.crop.x,
    cropY: props.panelMetaData.crop.y,
    width: props.panelMetaData.size.width,
    height: props.panelMetaData.size.height,
    zoom: props.panelMetaData.zoom,
    zIndex: props.panelMetaData.zIndex,
    colourCurve: props.panelMetaData.colourCurve.selected,
    temperatureDisplayEnabled: props.panelMetaData.temperatureDisplayEnabled
  };
}

export class Panel extends React.Component {
  constructor(props) {
    super(props);
    this.instanceRef = React.createRef();
    this.ratio = ratios(this.props.panelMetaData.compositeKey);
    this.max = this.max.bind(this);
    this.stopDrag = this.stopDrag.bind(this);
    this.timeoutId = null;

    if (!props.panelMetaData.colourCurve) {
      props.panelMetaData.colourCurve = {
        selected: 0
      };
    }
    props.panelMetaData.colourCurve.options = colourCurvesForKey(
      settings.workspace.colourCurves || {},
      props.panelMetaData.compositeKey
    );

    this.temperatureScale = getScaleForKey(
      this.props.panelMetaData.compositeKey
    );

    // Controls the length of time an image is displayed on screen
    // before moving on to the next frame. This value is updated
    // as playback progresses.
    this.playbackInterval = 0;
  }

  shouldComponentUpdate(nextProps, nextState) {
    const current = uniquePropsForPanel(this.props);
    const next = uniquePropsForPanel(nextProps);
    return !objectsDeepEqual(current, next);
  }

  max() {
    // Determine the state of isMaximized.
    // If maximizing: save current size & position to previousPosition. Then calculate the new size and position of the panel based on the worskspace and other maximized panels.
    // Otherwise: restore size & position from previousPosition
    const isMaximized = !this.props.panelMetaData.isMaximized;
    let { x, y } = this.props.panelMetaData.position;
    let { width, height } = this.props.panelMetaData.size;
    let previousPosition = null;

    if (isMaximized) {
      previousPosition = {
        x: x,
        y: y,
        height: height,
        width: width
      };

      // Determine the new position based on the current size of the workspace:
      const parentBounds = this.instanceRef.current
        .getParent()
        .getBoundingClientRect();
      const parentHeight = parentBounds.height;
      const parentWidth = parentBounds.width;
      const extraHeight = calculateExtraHeight(
        this.props.frameList.list,
        this.props.frame.list,
        this.props.panelMetaData.playbackPanelOpened
      );
      width = MAX_PANEL_SIZE * parentWidth;
      height = width / this.ratio + extraHeight;

      if (height > parentHeight) {
        height = MAX_PANEL_SIZE * parentHeight;
        width = (height - extraHeight) * this.ratio;
      }

      // By default, don't change the top left position of the panel
      x = previousPosition.x;
      y = previousPosition.y;

      // If we'd be bumping off the right side of the screen
      if (x + width > parentWidth) {
        // Make it as far to the right as we can without going off the screen
        x = parentWidth - width;
      }

      // If we'd be bumping off the bottom of the screen
      if (y + height > parentHeight) {
        // Make it as far to the bottom as we can without going off the screen
        y = parentHeight - height;
      }
    } else {
      x = this.props.panelMetaData.previousPosition.x;
      y = this.props.panelMetaData.previousPosition.y;
      height = this.props.panelMetaData.previousPosition.height;
      width = this.props.panelMetaData.previousPosition.width;
    }

    this.props.toggleMaximize({
      isMaximized,
      x,
      y,
      previousPosition,
      width,
      height
    });
  }

  stopDrag(e, d) {
    this.props.panelMoved(e, d);
  }

  handlePlaybackTick = () => {
    /*
      Calculates the desired playback interval for the next frame based on whether the next frame
      is the now/live frame. Calls the action dispatcher to start the process of advancing to the
      next frame.
    */

    this.playbackInterval =
      BASE_PLAYBACK_INTERVAL / this.props.panelMetaData.playbackSpeedModifier;

    const activeFrameIndex = this.props.frame.list.findIndex(
      f => f.id === this.props.frame.active.id
    );
    const nextFrameIndex =
      (activeFrameIndex + 1) % this.props.frame.list.length;
    const nextFrame = this.props.frame.list[nextFrameIndex];

    // If the next frame is the "NOW" or "Live" frame, we will "pause" playback for a certain amount of time.
    // Otherwise, the playback interval will default to the base value divided by the current speed modifier.
    // Note: the playback pause interval is unaffected by the speed modifier.
    if (nextFrame.id === this.props.frame.current.id) {
      this.playbackInterval = BASE_PLAYBACK_LAST_FRAME_INTERVAL;
    }

    // Advance to the next frame. Note that this function will not immediately advance the frame,
    // it will first start pre-loading the image for that frame, and only when the image successfully
    // downloads will the frame advance.
    this.props.advancePlaybackFrame();
  };

  setPlaybackInterval = () => {
    this.timeoutId = setTimeout(
      this.handlePlaybackTick.bind(this),
      this.playbackInterval
    );
  };

  UNSAFE_componentWillReceiveProps = nextProps => {
    /*
      This lifecycle function is called multiple times when playback starts
      or stops, due to various child components changing simultaneously.
      To avoid setting multiple timers at once, only change the timer state
      when the playback state is about to change.
      NOTE: the condition also checks if the panel is given populated image
      data, where none existed before. This is typical when the user first
      visits the workspaces page, as the image data is not yet available.
      When the panel is about to be given image data, and the panel is
      supposed to be in playback, THEN go ahead and start the cycle.
    */
    if (
      nextProps.panelMetaData.isPlayback !==
        this.props.panelMetaData.isPlayback ||
      (nextProps.panelMetaData.isPlayback &&
        nextProps.productData &&
        !this.props.productData)
    ) {
      if (nextProps.panelMetaData.isPlayback) {
        // if we're starting playback...
        this.setPlaybackInterval();
      } else {
        // otherwise, we're canceling playback
        clearTimeout(this.timeoutId);
        this.timeoutId = null;
      }
    } else if (
      this.props.panelMetaData.isPlayback &&
      nextProps.frame.active &&
      nextProps.frame.active.id !== this.props.frame.active.id
    ) {
      // This is called when we're already in playback, and frame has advanced. This occurs when the
      // next frame's image has successfully preloaded, and the new frame has been set as active.
      // When that occurs, we're ready to start the timer before advancing to the next frame and doing
      // the whole thing over again.
      this.setPlaybackInterval();
    }
  };

  componentDidMount = () => {
    /*
      Make sure we actually have image data,
      then check if we're in playback,
      then check if we've already started a timeout.
      NOTE: This condition will likely fail the first time the user
      visits the page. Playback will start in the `componentWillReceiveProps`
      method when the panel is finally given image data.
      This method is primarily used when the user switches workspaces.
    */
    if (
      this.props.productData &&
      this.props.panelMetaData.isPlayback &&
      !this.timeoutId
    ) {
      this.setPlaybackInterval();
    }
  };

  componentWillUnmount = () => {
    if (this.props.panelMetaData.isPlayback && this.timeoutId !== null) {
      clearTimeout(this.timeoutId);
    }
  };

  render() {
    // selected is a object with frameId/selectedImageId property/values.
    if (this.props.haveData) {
      // attach a callback directly to each frame while we have access to dispatch
      // Otherise I don't know how to wrap the frame ids into dispatch further down the tree
      this.props.frame.list.forEach(f => {
        f.setFrame = () => {
          this.props.dispatch(
            updateFrame(this.props.id, this.props.frameList.active.id, f.id)
          );
        };
      });
    }

    // Use an empty list if we don't have any frameLists yet
    const frameLists = this.props.productData
      ? this.props.productData.frameLists
      : [];

    const colourCurve = settings.workspace.canViewSatelliteColour
      ? {
          ...this.props.panelMetaData.colourCurve,
          onChange: this.props.onChangeColourCurve
        }
      : undefined;

    const panel = (
      <div id={`panel_${this.props.id}`} className="panel">
        <PanelHeader
          panelId={this.props.id}
          activeImageId={
            this.props.haveData ? this.props.image.active.id : null
          }
          compositeKey={this.props.panelMetaData.compositeKey}
          isLiveMode={this.props.panelMetaData.isLiveMode}
          playbackSpeed={{
            current: this.props.panelMetaData.playbackSpeedModifier,
            update: this.props.updatePanelPlaybackSpeed
          }}
          panelOptions={{
            isOpen: this.props.panelMetaData.playbackPanelOpened,
            toggle: this.props.togglePanelOptions
          }}
          // Optional
          closePanel={this.props.closePanel}
          maximize={{
            maximize: this.max,
            isMaximized: this.props.panelMetaData.isMaximized
          }}
          colourCurve={colourCurve}
          temperatureDisplayScale={this.temperatureScale}
          temperatureDisplayEnabled={
            this.props.panelMetaData.temperatureDisplayEnabled
          }
          updateTemperatureDisplay={this.props.updateTemperatureDisplay}
        />
        <FrameListSelector
          frameList={this.props.frameList}
          panelId={this.props.id}
        />
        <PanelImage
          hasFrameList={this.props.haveData}
          colourCurve={this.props.panelMetaData.colourCurve}
          activeImageId={
            this.props.haveData ? this.props.image.active.id : null
          }
          compositeKey={this.props.panelMetaData.compositeKey}
          isLiveMode={this.props.panelMetaData.isLiveMode}
          panelIsCurrent={this.props.panelIsCurrent}
          panelWidth={this.props.panelMetaData.size.width}
          panelRatio={this.ratio}
          crop={this.props.panelMetaData.crop}
          zoom={this.props.panelMetaData.zoom}
          panelId={this.props.id}
          position={this.props.panelMetaData.position}
          temperatureDisplayScale={this.temperatureScale}
          temperatureDisplayEnabled={
            this.props.panelMetaData.temperatureDisplayEnabled
          }
          geography={this.props.panelMetaData.compositeKey.geography}
        />
        <PanelFooter
          frameListId={
            this.props.haveData ? this.props.frameList.active.id : null
          }
          panelId={this.props.id}
          frame={this.props.frame}
          image={this.props.image}
          playback={{
            isEnabled: this.props.panelMetaData.isPlayback,
            toggle: this.props.setPanelPlayback
          }}
          nextFrame={this.props.updatePanelNextFrame}
          previousFrame={this.props.updatePanelPreviousFrame}
          liveMode={{
            isEnabled: this.props.panelMetaData.isLiveMode,
            toggle: this.props.setPanelLiveMode
          }}
        />
      </div>
    );

    const style = {
      zIndex: this.props.panelMetaData.zIndex,
      backgroundColor: "rgba(255, 255, 255, 0)",
      willChange: "transform"
    };

    const extraHeight = calculateExtraHeight(
      frameLists,
      this.props.frame.list,
      this.props.panelMetaData.playbackPanelOpened
    );
    return (
      <Rnd
        style={style}
        ref={this.instanceRef}
        lockAspectRatioExtraHeight={extraHeight}
        lockAspectRatio={this.ratio}
        position={this.props.panelMetaData.position}
        size={{
          width: this.props.panelMetaData.size.width,
          height:
            calculateImageHeight(
              this.props.panelMetaData.size.width,
              this.ratio
            ) + extraHeight
        }}
        bounds="parent"
        dragHandleClassName="panel-header"
        cancel=".not-draggable"
        onDragStop={this.stopDrag}
        minWidth="350"
        onMouseDown={this.props.touchPanel}
        onResize={(e, direction, ref, delta, position) => {
          if (this.props.panelMetaData.isMaximized) {
            this.props.toggleMaximize({
              isMaximized: false,
              x: position.x,
              y: position.y,
              previousPosition: null,
              width: parseInt(ref.style.width, 10),
              height: parseInt(ref.style.height, 10)
            });
          } else {
            let calculatedDelta = {
              width:
                parseInt(ref.style.width, 10) -
                this.props.panelMetaData.size.width,
              height:
                parseInt(ref.style.height, 10) -
                this.props.panelMetaData.size.height
            };
            this.props.panelResized(e, direction, ref, calculatedDelta);
          }
        }}
      >
        {panel}
      </Rnd>
    );
  }
}

const mapFrameState = (frames, userSelected) => {
  return frames.map(frame => {
    const currentImageId = defaultImage(frame.images).id;
    return {
      ...frame,
      currentImageId,
      activeImageId: userSelected.image[frame.id]
        ? userSelected.image[frame.id]
        : currentImageId
    };
  });
};

const mapStoreToProps = (state, props) => {
  const panelMetaData = getPanelProperties(props.id)(state);
  const calculatedSelectedData = calculateAndReturnSelectionForPanel(
    props.productData,
    panelMetaData.userSelected
  )(state);
  const time = getTime(state);
  const haveData = calculatedSelectedData.image !== undefined;

  return {
    time,
    panelMetaData,
    haveData,
    panelIsCurrent: calculatedSelectedData.panelIsCurrent,
    frameList: {
      list: haveData ? props.productData.frameLists : [],
      active: calculatedSelectedData.frameList,
      current: haveData
        ? defaultFrameList(time, props.productData.frameLists)
        : null
    },
    frame: {
      list: haveData
        ? mapFrameState(
            calculatedSelectedData.frameList.frames,
            panelMetaData.userSelected
          )
        : [],
      active: calculatedSelectedData.frame,
      current: haveData
        ? defaultFrame(time, calculatedSelectedData.frameList.frames)
        : null
    },
    image: {
      list: haveData ? calculatedSelectedData.frame.images : [],
      active: calculatedSelectedData.image,
      current: haveData
        ? defaultImage(calculatedSelectedData.frame.images)
        : null
    }
  };
};

const mapDispatchToProps = function (dispatch, ownProps) {
  // Manually use bindActionCreators here so we can additionally
  // pass the dispatch function itself as a prop.
  // We need do that because we are attaching a callback to each frame of the
  // selectedFrameList.
  const actions = bindActionCreators(
    {
      panelMoved,
      panelResized,
      toggleMaximize,
      touchPanel,
      updatePanelNextFrame,
      updatePanelPlaybackSpeed,
      updatePanelPreviousFrame,
      setPanelLiveMode,
      setPanelPlayback,
      updateColourCurve,
      closePanel,
      togglePanelOptions,
      updateTemperatureDisplay
    },
    dispatch
  );

  return {
    dispatch: dispatch,
    ...actions
  };
};

const mergeProps = (stateProps, dispatchProps, ownProps) => {
  return {
    ...ownProps,
    ...stateProps,
    panelMoved: dispatchProps.panelMoved.bind(null, ownProps.id),
    panelResized: dispatchProps.panelResized.bind(null, ownProps.id),
    toggleMaximize: dispatchProps.toggleMaximize.bind(null, ownProps.id),
    touchPanel: dispatchProps.touchPanel.bind(null, ownProps.id),
    advancePlaybackFrame: dispatchProps.updatePanelNextFrame.bind(
      null,
      ownProps.id,
      true
    ),
    setPanelPlayback: dispatchProps.setPanelPlayback.bind(
      null,
      ownProps.id,
      null
    ),
    setPanelLiveMode: dispatchProps.setPanelLiveMode.bind(
      null,
      ownProps.id,
      true
    ),
    updatePanelNextFrame: dispatchProps.updatePanelNextFrame.bind(
      null,
      ownProps.id,
      false
    ),
    updatePanelPlaybackSpeed: dispatchProps.updatePanelPlaybackSpeed,
    updatePanelPreviousFrame: dispatchProps.updatePanelPreviousFrame.bind(
      null,
      ownProps.id
    ),

    onChangeColourCurve: dispatchProps.updateColourCurve.bind(
      null,
      ownProps.id,
      stateProps.image.active ? stateProps.image.active.id : null
    ),

    closePanel: dispatchProps.closePanel.bind(null, ownProps.id),

    togglePanelOptions: dispatchProps.togglePanelOptions.bind(
      null,
      ownProps.id
    ),

    updateTemperatureDisplay: dispatchProps.updateTemperatureDisplay.bind(
      null,
      ownProps.id
    ),

    dispatch: dispatchProps.dispatch
  };
};

export default connect(mapStoreToProps, mapDispatchToProps, mergeProps)(Panel);
